// Offline asset-type search against the PowerSync local SQLite mirror.
//
// When the PowerSync capture adapter is active
// (VITE_USE_POWERSYNC_CAPTURE_CLIENT='true') the "Add asset" dialog
// routes here instead of GET /asset-types/search, which is
// unreachable with no network. The result SET must equal what the
// backend returns so field staff see the same asset types online and
// offline — asset counts are the contractual baseline and a divergent
// search would let a surveyor "not find" an asset that exists.
//
// Faithful mirror of apps/backend/src/classification/search-types.ts
// + search-query.ts. Three deliberate dialect adaptations, none of
// which change the result SET for the real (ASCII) asset taxonomy:
//
//   • Match — case-insensitive substring on `name` only. Backend uses
//     Postgres ILIKE; SQLite LIKE is case-insensitive for ASCII, which
//     is all the asset names contain. Literal %/_/\ in the query are
//     escaped identically and disarmed with `ESCAPE '\'`.
//   • Ordering — token-prefix matches first, then name, then id, so the
//     same rows survive the LIMIT when there are > take matches (the
//     reason the backend sorts server-side; see its ORDER BY comment).
//     SQLite has no POSIX `~*`, so the token-prefix test is replicated
//     with three LIKE probes covering the classifier's [\s-]+ token
//     boundary: start-of-string, space-preceded, hyphen-preceded.
//   • Scope — active contract only, mirroring the backend's
//     getActiveContract filter via the synced `contracts` table.

import type { GroupingConvention, UnitOfMeasure } from '@citycare/shared'

import {
  ASSET_TYPES_SEARCH_DEFAULT_TAKE,
  ASSET_TYPES_SEARCH_MAX_TAKE,
  ASSET_TYPE_SEARCH_MIN_QUERY,
  type AssetTypeSearchResponse,
  type AssetTypeSearchRow,
} from '../use-classification'

import { db } from './client'

// Minimal read surface so the core is unit-testable with a fake — the
// real PowerSyncDatabase singleton satisfies this.
export interface LocalSearchDb {
  getAll<T>(sql: string, parameters?: unknown[]): Promise<T[]>
}

export interface LocalSearchOptions {
  take?: number
  skip?: number
}

// Raw row off the SQLite mirror: booleans land as 0/1 integers and the
// page-independent match count rides along via COUNT(*) OVER ().
interface LocalSearchRawRow {
  id: string
  name: string
  code: string | null
  unitOfMeasure: UnitOfMeasure
  groupingConvention: GroupingConvention
  isFieldAdded: number
  requiresReview: number
  subSystemId: string
  subSystemName: string
  systemId: string
  systemName: string
  totalCount: number
}

interface LocalActiveContractRow {
  id: string
}

const ACTIVE_CONTRACT_SQL = `
  SELECT id
  FROM contracts
  WHERE is_active = 1
  ORDER BY id
`

// `ESCAPE '\'` — the backslash is doubled here so the string handed to
// SQLite is a single backslash; the WHERE/ORDER patterns escape literal
// %/_/\ in the user's query to the same convention.
const SEARCH_SQL = `
  SELECT
    t.id AS id,
    t.name AS name,
    t.code AS code,
    t.unit_of_measure AS unitOfMeasure,
    t.grouping_convention AS groupingConvention,
    t.is_field_added AS isFieldAdded,
    t.requires_review AS requiresReview,
    ss.id AS subSystemId,
    ss.name AS subSystemName,
    sy.id AS systemId,
    sy.name AS systemName,
    COUNT(*) OVER () AS totalCount
  FROM asset_types AS t
  JOIN asset_sub_systems AS ss ON ss.id = t.sub_system_id
  JOIN asset_systems AS sy ON sy.id = ss.system_id
  WHERE sy.contract_id = ?
    AND t.name LIKE ? ESCAPE '\\'
    AND t.rejected_at IS NULL
    AND ss.rejected_at IS NULL
    AND sy.rejected_at IS NULL
  ORDER BY
    CASE
      WHEN t.name LIKE ? ESCAPE '\\' THEN 0
      WHEN t.name LIKE ? ESCAPE '\\' THEN 0
      WHEN t.name LIKE ? ESCAPE '\\' THEN 0
      ELSE 1
    END,
    t.name COLLATE NOCASE,
    t.id
  LIMIT ? OFFSET ?
`

// Escape ILIKE/LIKE metacharacters so a literal %/_ in the query can't
// widen the match. Backslash first so the later passes don't re-escape
// the escape they just inserted. Mirrors backend escapeIlikeLiterals.
function escapeLikeLiterals(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_')
}

function resolveTake(take: number | undefined): number {
  const raw = take ?? ASSET_TYPES_SEARCH_DEFAULT_TAKE
  if (!Number.isFinite(raw) || raw <= 0) return ASSET_TYPES_SEARCH_DEFAULT_TAKE
  return Math.min(Math.floor(raw), ASSET_TYPES_SEARCH_MAX_TAKE)
}

function resolveSkip(skip: number | undefined): number {
  if (skip === undefined || !Number.isFinite(skip) || skip <= 0) return 0
  return Math.floor(skip)
}

// Exported for reuse by the offline classification-tree reader
// (load-classification-tree-local.ts) so both offline readers enforce
// the single-active-contract invariant identically. Extracting this
// plus ACTIVE_CONTRACT_SQL into a dedicated active-contract-local
// module is a deferred follow-up — kept here for now to avoid a
// refactor in this fix (AGENTS.md: smallest safe change).
export async function resolveActiveContractId(
  database: LocalSearchDb,
): Promise<string | null> {
  const rows = await database.getAll<LocalActiveContractRow>(ACTIVE_CONTRACT_SQL)
  if (rows.length === 0) return null
  if (rows.length > 1) {
    throw new Error(
      `Found ${rows.length} active contracts; expected exactly one. Phase 1 ` +
        'does not support multi-contract routing — set is_active = false on ' +
        'all but the tenant currently in use.',
    )
  }
  const [row] = rows
  if (!row) {
    throw new Error('resolveActiveContractId: unreachable empty row')
  }
  return row.id
}

function toSearchRow(row: LocalSearchRawRow): AssetTypeSearchRow {
  return {
    id: row.id,
    name: row.name,
    code: row.code,
    unitOfMeasure: row.unitOfMeasure,
    groupingConvention: row.groupingConvention,
    isFieldAdded: row.isFieldAdded === 1,
    requiresReview: row.requiresReview === 1,
    subSystemId: row.subSystemId,
    subSystemName: row.subSystemName,
    systemId: row.systemId,
    systemName: row.systemName,
  }
}

// Testable core — takes the db so unit tests can pass a fake.
export async function searchAssetTypesLocalWithDb(
  database: LocalSearchDb,
  query: string,
  options: LocalSearchOptions = {},
): Promise<AssetTypeSearchResponse> {
  const trimmed = query.trim()
  // Matches the backend's 2-char minimum; the dialog guards this too,
  // so returning empty (rather than throwing) keeps the offline path
  // quiet for the sub-threshold keystrokes that slip through.
  if (trimmed.length < ASSET_TYPE_SEARCH_MIN_QUERY) {
    return { results: [], total: 0 }
  }

  const activeContractId = await resolveActiveContractId(database)
  if (activeContractId === null) {
    return { results: [], total: 0 }
  }

  const escaped = escapeLikeLiterals(trimmed)
  const containsPattern = `%${escaped}%`
  const startPattern = `${escaped}%`
  const spacePattern = `% ${escaped}%`
  const hyphenPattern = `%-${escaped}%`

  const rows = await database.getAll<LocalSearchRawRow>(SEARCH_SQL, [
    activeContractId,
    containsPattern,
    startPattern,
    spacePattern,
    hyphenPattern,
    resolveTake(options.take),
    resolveSkip(options.skip),
  ])

  return {
    results: rows.map(toSearchRow),
    total: rows[0]?.totalCount ?? 0,
  }
}

// Production wrapper matching the AddAssetDialog `search` prop shape.
export async function searchAssetTypesLocal(
  query: string,
  init: { signal: AbortSignal },
): Promise<AssetTypeSearchResponse> {
  const response = await searchAssetTypesLocalWithDb(db, query)
  // A superseded keystroke aborts its signal; throw the same
  // AbortError shape fetch does so the dialog's isAbortError swallow
  // discards the stale result instead of overwriting the newer one.
  if (init.signal.aborted) {
    throw new DOMException('Aborted', 'AbortError')
  }
  return response
}
